import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, MapPin, Clock, Star, CheckCircle, Calendar, Shield, Award } from 'lucide-react';

const DentistNearMe = () => {
  const nearbyAreas = [
    { name: 'Fort Worth', distance: '0 mi', slug: '/top-dentists-fort-worth/' },
    { name: 'Benbrook', distance: '5 mi', slug: '/dentist-near-benbrook/' },
    { name: 'White Settlement', distance: '4 mi', slug: '/general-dentistry-white-settlement/' },
    { name: 'Westover Hills', distance: '3 mi', slug: '/dentist-near-westover-hills/' },
    { name: 'River Oaks', distance: '4 mi', slug: '/general-dentistry-river-oaks/' },
    { name: 'Lake Worth', distance: '8 mi', slug: '/general-dentistry-lake-worth/' },
    { name: 'Aledo', distance: '12 mi', slug: '/dentist-near-aledo/' },
    { name: 'Haltom City', distance: '10 mi', slug: '/general-dentistry-haltom-city/' },
  ];

  const services = [
    { name: 'General Dentistry', icon: '🦷', desc: 'Cleanings, exams, fillings' },
    { name: 'Dental Implants', icon: '🔩', desc: 'Permanent tooth replacement' },
    { name: 'Emergency Care', icon: '🚨', desc: 'Same-day appointments' },
    { name: 'Cosmetic Dentistry', icon: '✨', desc: 'Veneers, whitening, makeovers' },
    { name: 'Invisalign', icon: '😁', desc: 'Clear aligner therapy' },
    { name: 'LANAP Laser', icon: '💡', desc: 'Advanced gum treatment' },
  ];

  return (
    <>
      <Helmet>
        <title>Dentist Near Me | Fort Worth Dental - Open Now, Same-Day Appointments</title>
        <meta name="description" content="Looking for a dentist near me? Fort Worth Dental offers same-day appointments, emergency care, and comprehensive dental services. Conveniently located near Bryant Irvin Rd and I-20. Call (817) 985-3337!" />
        <meta name="keywords" content="dentist near me, dentist nearby, local dentist, dentist open now, emergency dentist near me, Fort Worth dentist" />
        <link rel="canonical" href="https://fortworthdental.com/dentist-near-me" />
        <meta property="og:title" content="Dentist Near Me | Fort Worth Dental" />
        <meta property="og:description" content="Find a trusted dentist near you in Fort Worth. Same-day appointments available. Call (817) 985-3337." />
        <meta property="og:url" content="https://fortworthdental.com/dentist-near-me" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dentist",
            "name": "Fort Worth Dental",
            "description": "Trusted dentist near you in Fort Worth, TX. Same-day appointments, emergency care, and comprehensive dental services.",
            "url": "https://fortworthdental.com/dentist-near-me",
            "telephone": "+1-817-985-3337",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "4620 Citylake Blvd W",
              "addressLocality": "Fort Worth",
              "addressRegion": "TX",
              "postalCode": "76132",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 32.6834,
              "longitude": -97.4261
            },
            "openingHoursSpecification": [
              { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"], "opens": "08:00", "closes": "17:00" },
              { "@type": "OpeningHoursSpecification", "dayOfWeek": "Friday", "opens": "08:00", "closes": "12:00" }
            ],
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <MapPin className="w-5 h-5" />
              <span>4620 Citylake Blvd W, Fort Worth, TX</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Looking for a <span className="text-yellow-300">Dentist Near You</span>?
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-teal-100">
              Fort Worth Dental is conveniently located and accepting new patients.
              Same-day appointments available!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:817-985-3337"
                className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-4 px-8 rounded-lg text-lg transition"
              >
                <Phone className="w-6 h-6" />
                (817) 985-3337
              </a>
              <Link
                to="/appointments/"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-teal-700 font-bold py-4 px-8 rounded-lg text-lg transition"
              >
                <Calendar className="w-6 h-6" />
                Book Online
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-300" />
                <span>Mon-Thu: 8am-5pm | Fri: 8am-12pm</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-300" />
                <span>4.9 Stars (500+ Reviews)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Why Patients Choose Us as Their <span className="text-teal-600">Local Dentist</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Clock className="w-8 h-8" />, title: 'Same-Day Appointments', desc: 'Emergency? We\'ll see you today.' },
              { icon: <Shield className="w-8 h-8" />, title: 'Insurance Accepted', desc: 'Most major plans welcome.' },
              { icon: <Award className="w-8 h-8" />, title: '25+ Years Experience', desc: 'Trusted by thousands of families.' },
              { icon: <MapPin className="w-8 h-8" />, title: 'Easy to Find', desc: 'Off Citylake Blvd, near I-20.' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 rounded-xl bg-gray-50 hover:shadow-lg transition">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 text-teal-600 rounded-full mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Dental Services Near You
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Comprehensive dental care all in one convenient location
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.map((service, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition flex items-start gap-4">
                <span className="text-4xl">{service.icon}</span>
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{service.name}</h3>
                  <p className="text-gray-600 text-sm">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Serving Patients From These Nearby Areas
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Conveniently located to serve the greater Fort Worth area
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {nearbyAreas.map((area, idx) => (
              <Link
                key={idx}
                to={area.slug}
                className="bg-gray-50 hover:bg-teal-50 p-4 rounded-lg text-center transition group"
              >
                <div className="font-semibold text-gray-800 group-hover:text-teal-600">{area.name}</div>
                <div className="text-sm text-gray-500">{area.distance} away</div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/areas-we-serve/" className="text-teal-600 hover:text-teal-700 font-semibold">
              View All Service Areas →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Visit Your New Dentist?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Schedule your appointment today and experience the Fort Worth Dental difference.
            New patients welcome!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:817-985-3337"
              className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-4 px-8 rounded-lg text-lg transition"
            >
              <Phone className="w-6 h-6" />
              Call (817) 985-3337
            </a>
            <Link
              to="/appointments/"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-teal-700 font-bold py-4 px-8 rounded-lg text-lg transition"
            >
              Book Online Now
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DentistNearMe;
